/**
 * MasterySpectrum — the Padronanza "signature" element (design spec:
 * analytics-design-spec.md § "Spettro di padronanza").
 *
 * The whole course index rendered as one horizontal segmented strip, one
 * segment per chapter:
 *   - segment width ∝ chapter.questionsTotal (real weight in the course,
 *     chapters don't all carry the same number of questions);
 *   - segment color = mastery state (same semantic scale as Meter);
 *   - low-reliability chapters (answered < cutoff) render the same color at
 *     reduced opacity + a diagonal hatch — "provisional", never solid.
 * Click/tap a segment -> scrolls to that chapter's row (see MasteryPanel).
 *
 * This is deliberately the one bold element on the page; everything else
 * (chapter rows, Meter, badges) stays quiet and disciplined.
 */

import * as React from 'react'
import { clsx } from 'clsx'
import type { ChapterMastery, MasteryState } from '@arianna/reader-core'
import { METER_HATCH_BACKGROUND_IMAGE } from '@arianna/design-system/Meter'

// Mirrors Meter.variants.ts meterFillVariants' `state` -> color mapping
// (strong/consolidate/weak/insufficient -> green/gold/red/ink-3). Kept as a
// small local map rather than reusing the cva output directly: the segment
// shape (flex-basis strip, square corners) differs enough from the Meter
// fill bar that sharing the class string would fight itself more than a
// 4-line literal duplication costs.
const SEGMENT_COLOR_CLASS: Record<MasteryState, string> = {
  strong: 'bg-green',
  consolidate: 'bg-gold',
  weak: 'bg-red',
  insufficient: 'bg-ink-3',
}

const STATE_LABEL: Record<MasteryState, string> = {
  strong: 'Solido',
  consolidate: 'Da consolidare',
  weak: 'Fragile',
  insufficient: 'Ancora pochi dati',
}

export interface MasterySpectrumProps {
  chapters: ChapterMastery[]
  onSegmentClick: (chapterId: string) => void
  className?: string
}

export function MasterySpectrum({ chapters, onSegmentClick, className }: MasterySpectrumProps) {
  // Rendered in document order (chapter.order), NOT the weak-first order used
  // by the list below — the spectrum is "the spine of the book", it must
  // read left-to-right like the course itself.
  const documentOrder = React.useMemo(
    () => [...chapters].sort((a, b) => a.order - b.order),
    [chapters],
  )

  return (
    <div
      role="group"
      aria-label="Spettro di padronanza per capitolo"
      className={clsx(
        'flex w-full gap-0.5 overflow-x-auto rounded-md border border-line bg-surface-2 p-0.5',
        className,
      )}
    >
      {documentOrder.map((chapter) => {
        const provisional = chapter.answered > 0 && !chapter.reliable
        const label = `${chapter.chapterId} · ${chapter.title} — ${STATE_LABEL[chapter.state]}${
          provisional ? ` (solo ${chapter.answered} risposte, indicativo)` : ''
        }`
        const segmentStyle: React.CSSProperties = {
          flex: `${Math.max(chapter.questionsTotal, 1)} 0 0%`,
          ...(provisional ? { backgroundImage: METER_HATCH_BACKGROUND_IMAGE } : {}),
        }
        return (
          <button
            key={chapter.chapterId}
            type="button"
            title={label}
            aria-label={label}
            onClick={() => onSegmentClick(chapter.chapterId)}
            style={segmentStyle}
            className={clsx(
              'h-7 min-w-[10px] shrink-0 cursor-pointer rounded-[3px] border-none transition-opacity',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
              SEGMENT_COLOR_CLASS[chapter.state],
              provisional && 'opacity-[0.55]',
            )}
          />
        )
      })}
    </div>
  )
}
